import { useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { BottomSheetHeights, formatDate } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { ModulesInfo } from "../../constants/modules"
import { useDocumentTypes } from "../../hooks/queries/useDocumentTypes"

import * as Styled from "./DocumentInfo.styled"

export const DocumentInfo = (props) => {

    const {
        className,
        document,
        onClose,
    } = props

    const [currentBottomSheetHeight, setCurrentBottomSheetHeight] = useState(BottomSheetHeights.HALF)

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()

    const { data: documentTypes } = useDocumentTypes()

    const documentType = useMemo(() => {
        if (!document?.document_type || !documentTypes) {
            return null
        }
        return documentTypes?.find(type => type.name === document.document_type)
    }, [document?.document_type, documentTypes])

    const AttachmentIcon = useMemo(() => {
        return ModulesInfo[document.attached_object_content_type ?? document.linear_location_type.toLowerCase()]?.icon
    }, [document])

    const documentFormat = useMemo(() => {
        return document.filename.split(".").length >= 2 ? document.filename.split(".").pop() : ""
    }, [document])

    const tags = useMemo(() => {
        return document.tags.map(tag => (
            <Styled.Chip key={tag} label={tag} />
        ))
    }, [document.tags])

    const infoContent = useMemo(() => {
        const formatAndDate =
            <>
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.documentInfo.format")}</Styled.InfoRowTitle>
                    <Styled.InfoFormat variant="Desktop & Mobile/Body/Regular">{documentFormat}</Styled.InfoFormat>
                </Styled.InfoRow>
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Regular">{t(I18N_NAMESPACE, "components.documentInfo.date")}</Styled.InfoRowTitle>
                    <Styled.InfoFormat variant="Desktop & Mobile/Body/Regular">{formatDate(document.date, "dd/MM/yyyy")}</Styled.InfoFormat>
                </Styled.InfoRow>
            </>

        return <Styled.InfoContentContainer isMobile={isMobile}>
            <Styled.Header isMobile={isMobile}>
                <Styled.InfoIcon />
                <Typography variant="Desktop & Mobile/Heading/H1">{t(I18N_NAMESPACE, "components.documentReaderModal.menu.info")}</Typography>
                {!isMobile && <Styled.IconButton id="close-button" onClick={onClose}>
                    <Styled.CloseIcon />
                </Styled.IconButton>}
            </Styled.Header>
            <Styled.Content>
                {isMobile ? <Styled.FormatDateWrapper>{formatAndDate}</Styled.FormatDateWrapper> : formatAndDate}
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.documentInfo.type")}</Styled.InfoRowTitle>
                    <Styled.RowContainer>
                        {documentType && <Styled.TypeIcon src={documentType.icon} alt={documentType.name} />}
                        <Typography variant="Desktop & Mobile/Body/Regular">{document.document_type}</Typography>
                    </Styled.RowContainer>
                </Styled.InfoRow>
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.documentInfo.attachment")}</Styled.InfoRowTitle>
                    <Styled.RowContainer>
                        <Styled.AttachmentIconWrapper>
                            {AttachmentIcon && <AttachmentIcon sx={{ width: 16, height: 16 }} />}
                        </Styled.AttachmentIconWrapper>
                        <Typography variant="Desktop & Mobile/Body/Regular">{t(I18N_NAMESPACE, ModulesInfo[document.attached_object_content_type ?? document.linear_location_type.toLowerCase()].labelKey)}</Typography>
                    </Styled.RowContainer>
                </Styled.InfoRow>
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.documentInfo.owner")}</Styled.InfoRowTitle>
                    <Styled.Chip label={document.created_by} />
                </Styled.InfoRow>
                <Styled.InfoRow>
                    <Styled.InfoRowTitle variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.documentInfo.tags")}</Styled.InfoRowTitle>
                    <Styled.TagsList>
                        {tags}
                    </Styled.TagsList>
                </Styled.InfoRow>
            </Styled.Content>
        </Styled.InfoContentContainer>
    }, [
        AttachmentIcon,
        document,
        documentFormat,
        documentType,
        onClose,
        tags,
        isMobile,
        t,
    ])

    if (isMobile) {
        return <Styled.BottomSheet
            disablePortal={false}
            height={currentBottomSheetHeight}
            setHeight={setCurrentBottomSheetHeight}
            onClose={onClose}
        >
            {infoContent}
        </Styled.BottomSheet>
    } else {
        return <Styled.Container className={className}>
            {infoContent}
        </Styled.Container>
    }

}

DocumentInfo.propTypes = {
    className: PropTypes.string,
    document: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
}